import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { bankReconApi, type BankStatement, type ColumnOverride, type ParsePreviewResult } from '../api/bankRecon';
import BankStatementMappingModal from '../components/BankStatementMappingModal';
import { FeatureGate } from '../components/FeatureGate';
import { ApiError } from '../api/client';

const fmtN = (n: number) => '₹' + Math.abs(n ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });

// The statement-list half of what used to be ReportsPage.tsx's inline Bank
// Statement Reconciliation block — now its own page (route /banking), per
// the plan this shipped from (TRACKER.md), so a "Suggested Ledger" workflow
// (see BankTransactionsPage.tsx) has room for a proper bulk-operations bar
// and status filters without cramming into the Reports tab layout.
export default function BankingPage() {
  const navigate = useNavigate();
  const [bankStatements, setBankStatements] = useState<BankStatement[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState<{ text: string; level: 'success' | 'warning' } | null>(null);
  const [search, setSearch] = useState('');
  const [bankFilter, setBankFilter] = useState('');

  // Held separately from the file input, which clears itself after each
  // change event — needed to retry the same file once a password is entered.
  const [pendingPdfFile, setPendingPdfFile] = useState<File | null>(null);
  const [pdfPassword, setPdfPassword] = useState('');
  const [pdfPasswordError, setPdfPasswordError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  // Set right before programmatically clicking the file input from the
  // "Review column mapping…" link — read once in the input's onChange, then
  // reset, so the same hidden input can serve both the default one-click
  // upload and the always-available manual-review path.
  const forceMappingReviewRef = useRef(false);
  const [mappingModal, setMappingModal] = useState<{ file: File; password?: string; preview: ParsePreviewResult } | null>(null);

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function refresh() {
    setLoading(true);
    try {
      const stmts = await bankReconApi.listStatements();
      setBankStatements(stmts);
    } finally {
      setLoading(false);
    }
  }

  // The actual commit — persists the statement and refreshes the list. Used
  // both by the fast path (auto-detection confident enough to skip the
  // mapping modal) and as the mapping modal's onConfirm.
  async function commitBankStatementUpload(file: File, password: string | undefined, columnOverrides?: ColumnOverride[], confirmedByUser = false) {
    const res = await bankReconApi.upload(file, undefined, undefined, password, columnOverrides, confirmedByUser);
    const hasSkipped = res.skippedRows > 0;
    const hasVerificationIssue = res.verification.checked && !res.verification.passed;
    const text = `Uploaded ${res.entryCount} entries (${res.bankName})` +
      (res.ocrUsed ? ' — read via OCR, please double-check amounts and dates' : '') +
      (hasSkipped ? ` — ${res.skippedRows} row${res.skippedRows === 1 ? '' : 's'} skipped (unrecognized date/format)` : '') +
      (hasVerificationIssue ? ` — ${res.verification.issues.join(' ')}` : '');
    setMsg({ text, level: hasSkipped || hasVerificationIssue || res.ocrUsed ? 'warning' : 'success' });
    setPendingPdfFile(null); setPdfPassword('');
    await refresh();
  }

  // Always previews first (parses, never persists) — if the file's format
  // wasn't confidently recognized, or the parse looks suspect (see
  // bank-statement-utils.ts's assessNarrationQuality()/assessRowLoss()), or
  // the user explicitly asked to review (the "Review column mapping…"
  // link), this pauses on BankStatementMappingModal instead of committing
  // immediately.
  async function doUpload(file: File, password?: string, forceReview = false) {
    setLoading(true); setMsg(null); setPdfPasswordError(null);
    try {
      const preview = await bankReconApi.previewUpload(file, password);
      // OCR-derived output always pauses on the review modal, regardless of
      // parseSuspect — a misread digit (0/O, 1/I, 5/S) in an amount
      // column looks like a perfectly plausible value and won't trip that
      // heuristic, so this is the one signal that must force review on its
      // own. See CLAUDE.md's bank-statement-parsing note.
      const uncertain = forceReview || preview.parseSuspect || preview.ocrUsed || preview.bankName === 'Generic' || preview.bankName === 'PDF Import';
      setPendingPdfFile(null); setPdfPassword('');
      if (uncertain) {
        setMappingModal({ file, password, preview });
      } else {
        await commitBankStatementUpload(file, password, preview.appliedMapping);
      }
    } catch (err) {
      if (err instanceof ApiError && (err.code === 'PDF_PASSWORD_REQUIRED' || err.code === 'PDF_WRONG_PASSWORD')) {
        setPendingPdfFile(file);
        setPdfPasswordError(err.code === 'PDF_WRONG_PASSWORD' ? err.message : null);
      } else {
        setMsg({ text: err instanceof Error ? err.message : 'Upload failed', level: 'warning' });
        setPendingPdfFile(null); setPdfPassword('');
      }
    } finally {
      setLoading(false);
    }
  }

  async function deleteStatement(s: BankStatement) {
    if (!window.confirm(`Delete the ${s.bankName} statement uploaded on ${s.uploadedAt?.slice(0, 10)} (${s._count?.entries ?? 0} entries)? This cannot be undone.`)) return;
    setLoading(true); setMsg(null);
    try {
      await bankReconApi.deleteStatement(s.id);
      setBankStatements(prev => (prev ?? []).filter(x => x.id !== s.id));
      setMsg({ text: `Deleted ${s.bankName} statement`, level: 'success' });
    } catch (err) {
      setMsg({ text: err instanceof Error ? err.message : 'Delete failed', level: 'warning' });
    } finally {
      setLoading(false);
    }
  }

  const stmtSearch = search.trim().toLowerCase();
  const banks = Array.from(new Set((bankStatements ?? []).map(s => s.bankName))).sort();
  const filteredStatements = (bankStatements ?? []).filter(s => {
    if (bankFilter && s.bankName !== bankFilter) return false;
    if (stmtSearch && !s.bankName.toLowerCase().includes(stmtSearch) && !(s.accountNumber ?? '').toLowerCase().includes(stmtSearch)) return false;
    return true;
  });

  return (
    <FeatureGate feature="bankReconciliation">
      <div className="page">
        <div className="page-header">
          <h1 className="page-title">Banking</h1>
          <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', flexWrap: 'wrap' }}>
            {msg && <span style={{ fontSize: '0.78rem', color: msg.level === 'warning' ? 'var(--warning)' : 'var(--success)' }}>{msg.text}</span>}
            {pendingPdfFile && (
              <form
                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}
                onSubmit={e => { e.preventDefault(); doUpload(pendingPdfFile, pdfPassword); }}
              >
                <span style={{ fontSize: '0.78rem', color: pdfPasswordError ? 'var(--danger)' : 'var(--text-2)' }}>
                  {pdfPasswordError ?? `"${pendingPdfFile.name}" is password protected:`}
                </span>
                <input
                  type="password" autoFocus value={pdfPassword} onChange={e => setPdfPassword(e.target.value)}
                  placeholder="PDF password" style={{ fontSize: '0.78rem', padding: '0.3rem 0.5rem', width: 140 }}
                />
                <button type="submit" className="btn btn-primary btn-sm" style={{ fontSize: '0.78rem' }} disabled={loading || !pdfPassword}>
                  Unlock &amp; Upload
                </button>
                <button
                  type="button" className="btn btn-ghost btn-sm" style={{ fontSize: '0.78rem' }}
                  onClick={() => { setPendingPdfFile(null); setPdfPassword(''); setPdfPasswordError(null); }}
                >Cancel</button>
              </form>
            )}
            <button className="btn btn-primary btn-sm" onClick={() => { forceMappingReviewRef.current = false; fileRef.current?.click(); }} disabled={loading}>
              Upload CSV / PDF / OFX
            </button>
            <button
              className="btn btn-ghost btn-sm" disabled={loading}
              onClick={() => { forceMappingReviewRef.current = true; fileRef.current?.click(); }}
              title="Pick a file and always review its detected columns before uploading"
            >Review column mapping…</button>
            <input ref={fileRef} type="file" accept=".csv,.ofx,.qif,.pdf" style={{ display: 'none' }} onChange={async e => {
              const file = e.target.files?.[0];
              if (!file) return;
              const forceReview = forceMappingReviewRef.current;
              forceMappingReviewRef.current = false;
              await doUpload(file, undefined, forceReview);
              if (fileRef.current) fileRef.current.value = '';
            }} />
          </div>
        </div>

        <div className="card">
          {bankStatements === null ? (
            <div className="loading-overlay"><span className="spinner" /></div>
          ) : bankStatements.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">🏦</div>
              <div className="empty-state-text">Upload a bank statement — CSV (HDFC/ICICI/SBI/Kotak), PDF, or OFX/QIF — to reconcile it against your recorded payments and get category suggestions for the rest.</div>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', gap: '0.75rem', padding: '1rem 1rem 0', flexWrap: 'wrap' }}>
                <input
                  value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="Search by bank or account number…" style={{ maxWidth: 300, fontSize: '0.85rem' }}
                />
                <select value={bankFilter} onChange={e => setBankFilter(e.target.value)} style={{ width: 'auto', fontSize: '0.85rem' }}>
                  <option value="">All banks</option>
                  {banks.map(b => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table>
                  <thead>
                    <tr>
                      <th>Bank</th><th>Account</th><th>Period</th>
                      <th style={{ textAlign: 'right' }}>Total</th>
                      <th style={{ textAlign: 'right' }}>Pending</th>
                      <th style={{ textAlign: 'right' }}>Saved</th>
                      <th style={{ textAlign: 'right' }}>Suggestion</th>
                      <th style={{ textAlign: 'right' }}>Closing Balance</th>
                      <th>Uploaded</th><th>Status</th><th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStatements.length === 0 ? (
                      <tr><td colSpan={11} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '1.5rem' }}>No statements match your search/filter</td></tr>
                    ) : filteredStatements.map(s => (
                      <tr key={s.id}>
                        <td>{s.bankName}</td>
                        <td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{s.accountNumber ?? '—'}</td>
                        <td style={{ fontSize: '0.8rem' }}>{s.fromDate?.slice(0, 10)} → {s.toDate?.slice(0, 10)}</td>
                        <td style={{ textAlign: 'right' }}>{s._count?.entries ?? 0}</td>
                        <td style={{ textAlign: 'right', color: 'var(--warning)' }}>{s.pendingCount ?? 0}</td>
                        <td style={{ textAlign: 'right', color: 'var(--success)' }}>{s.confirmedCount ?? 0}</td>
                        <td style={{ textAlign: 'right' }}>{s.suggestionCount ?? 0}</td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>{s.closingBalance != null ? fmtN(Number(s.closingBalance)) : '—'}</td>
                        <td style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{s.uploadedAt?.slice(0, 10)}</td>
                        <td><span className="badge badge-green">Complete</span></td>
                        <td style={{ display: 'flex', gap: '0.4rem' }}>
                          <button className="btn btn-ghost btn-sm" onClick={() => navigate(`/banking/${s.id}`)}>View</button>
                          <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)' }} disabled={loading} onClick={() => deleteStatement(s)}>Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {mappingModal && (
          <BankStatementMappingModal
            file={mappingModal.file}
            password={mappingModal.password}
            initialPreview={mappingModal.preview}
            onClose={() => setMappingModal(null)}
            onConfirm={overrides => commitBankStatementUpload(mappingModal.file, mappingModal.password, overrides, true)}
          />
        )}
      </div>
    </FeatureGate>
  );
}
